/** Own the whole drag, including iframe crossings and interrupted gestures. */
export function startPanelResize(
  target: HTMLElement,
  pointerId: number,
  direction: 'senkrecht' | 'waagrecht',
  move: (event: PointerEvent) => void,
): () => void {
  const doc = target.ownerDocument;
  const win = doc.defaultView!;
  const cursor = direction === 'senkrecht' ? 'col-resize' : 'row-resize';
  const previousSelection = doc.body.style.userSelect;
  const previousCursor = doc.body.style.cursor;
  const shield = doc.createElement('div');
  shield.dataset.resizeShield = 'true';
  Object.assign(shield.style, {
    position: 'fixed', inset: '0', zIndex: '2147483647', cursor,
    touchAction: 'none', userSelect: 'none',
  });
  // Electron guests have their own event handling as well as ordinary iframes.
  const guests = Array.from(doc.querySelectorAll<HTMLElement>('iframe, webview'))
    .map(element => ({ element, value: element.style.getPropertyValue('pointer-events'),
      priority: element.style.getPropertyPriority('pointer-events') }));
  guests.forEach(({ element }) => element.style.setProperty('pointer-events', 'none', 'important'));
  doc.body.style.userSelect = 'none';
  doc.body.style.cursor = cursor;
  doc.body.appendChild(shield);
  let ended = false;
  const finish = () => {
    if (ended) return;
    ended = true;
    win.removeEventListener('pointermove', onMove, true);
    win.removeEventListener('pointerup', onEnd, true);
    win.removeEventListener('pointercancel', onEnd, true);
    win.removeEventListener('blur', finish);
    win.removeEventListener('keydown', onKey, true);
    doc.removeEventListener('visibilitychange', onVisibility);
    target.removeEventListener('lostpointercapture', onEnd);
    if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
    shield.remove();
    guests.forEach(({ element, value, priority }) => {
      if (value) element.style.setProperty('pointer-events', value, priority);
      else element.style.removeProperty('pointer-events');
    });
    doc.body.style.userSelect = previousSelection;
    doc.body.style.cursor = previousCursor;
  };
  const onMove = (event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    if (!(event.buttons & 1)) { finish(); return; }
    event.preventDefault();
    move(event);
  };
  const onEnd = (event: PointerEvent) => { if (event.pointerId === pointerId) finish(); };
  const onVisibility = () => { if (doc.hidden) finish(); };
  const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') finish(); };
  win.addEventListener('pointermove', onMove, true);
  win.addEventListener('pointerup', onEnd, true);
  win.addEventListener('pointercancel', onEnd, true);
  win.addEventListener('blur', finish);
  win.addEventListener('keydown', onKey, true);
  doc.addEventListener('visibilitychange', onVisibility);
  target.addEventListener('lostpointercapture', onEnd);
  try { target.setPointerCapture(pointerId); } catch { /* Shield also owns the drag if capture is unavailable. */ }
  return finish;
}
